import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MailCheck, X } from 'lucide-react';

import { register, resendVerificationEmail } from '../api/authService';
import AppleAuthButton from '../components/AppleAuthButton';
import config from '../config/config';
import { useNativeCaps } from '../context/NativeCapsContext';
import { useLocale } from '../context/LocaleContext';
import { canUseNativeAppleAuth, getAuthQueryErrorMessage } from '../utils/appleAuth';

const inputClassName = 'relative block w-full rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 sm:text-sm transition-all shadow-inner backdrop-blur-sm';
const AI_CONSENT_VERSION = '2026-05-26';

const GoogleLogo = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" data-testid="google-logo">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26-.19-.58z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const AiConsentModal = ({ onAgree, onDecline, t }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="ai-consent-title">
    <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-cyan-300/20 bg-slate-950 p-6 text-gray-300 shadow-2xl shadow-cyan-950/50 sm:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">{t('auth.consentTag', 'Data & Privacy')}</p>
          <h2 id="ai-consent-title" className="mt-2 text-2xl font-black text-white">{t('auth.consentTitle', 'AI Data Processing')}</h2>
        </div>
        <button
          type="button"
          onClick={onDecline}
          className="rounded-full border border-white/10 bg-white/5 p-2 text-gray-300 hover:bg-white/10 hover:text-white"
          aria-label={t('auth.consentClose', 'Close AI data processing details')}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <p className="mt-5 leading-7 text-gray-200">
        {t(
          'auth.consentIntro',
          'Forza uses trusted AI and speech providers to generate personalized quests, coach you, verify completions, review exercise form, and power related AI features.'
        )}
      </p>

      <div className="mt-6 space-y-6 text-sm leading-6">
        <section>
          <h3 className="font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentProcessed', 'Data that may be processed')}</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>{t('auth.consentProcessedItems.profile', 'Fitness goals, preferences, profile context, and onboarding answers.')}</li>
            <li>{t('auth.consentProcessedItems.quests', 'Quest plans, quest activity, completion status, and verification context.')}</li>
            <li>{t('auth.consentProcessedItems.text', 'Text you submit to AI-powered features.')}</li>
            <li>{t('auth.consentProcessedItems.voice', 'Voice recordings and transcripts submitted to Voice Coach or voice verification.')}</li>
            <li>{t('auth.consentProcessedItems.photos', 'Photos submitted for meal or quest verification.')}</li>
            <li>{t('auth.consentProcessedItems.exercise', 'Exercise frames submitted for form checks.')}</li>
            <li>{t('auth.consentProcessedItems.diagnostics', 'App, device, and diagnostic context needed to operate, secure, and troubleshoot these features.')}</li>
          </ul>
        </section>

        <section>
          <h3 className="font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentProviders', 'AI and speech providers')}</h3>
          <p className="mt-3">
            {t('auth.consentProvidersBody', 'Forza may use Google, Groq, OpenRouter, DeepSeek, OpenAI, and local or on-device providers where available.')}
          </p>
        </section>

        <section>
          <h3 className="font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentInfrastructure', 'Infrastructure providers')}</h3>
          <p className="mt-3">
            {t('auth.consentInfrastructureBody', 'Related data may also be handled by providers used for hosting, storage, media delivery, monitoring, subscriptions, advertising consent and reward attribution, and security, including Render, MongoDB Atlas, Cloudflare R2, ImageKit, Better Stack, Apple, RevenueCat, and Google AdMob where relevant.')}
          </p>
        </section>

        <section>
          <h3 className="font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentWhenSent', 'When data is sent')}</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>{t('auth.consentWhenSentItems.onboarding', 'During onboarding and quest generation.')}</li>
            <li>{t('auth.consentWhenSentItems.coach', 'When you use AI Coach or Voice Coach.')}</li>
            <li>{t('auth.consentWhenSentItems.verification', 'When you submit voice notes, photos, or exercise frames for verification or form checks.')}</li>
            <li>{t('auth.consentWhenSentItems.planning', 'When AI-powered features need app context to respond, verify progress, or plan quests.')}</li>
          </ul>
        </section>

        <p>
          {t('auth.consentFooter', "Forza does not sell your personal data or share it for third-party advertising or profiling. These providers process data only as needed to operate Forza's services and must provide the same or equal protection described in our Privacy Policy.")}
        </p>
      </div>

      <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onDecline}
          className="rounded-xl border border-white/15 px-5 py-3 text-sm font-bold text-gray-200 hover:bg-white/10"
        >
          {t('auth.consentDecline', 'Decline')}
        </button>
        <button
          type="button"
          onClick={onAgree}
          className="rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-3 text-sm font-bold text-white hover:from-cyan-400 hover:to-blue-500"
        >
          {t('auth.consentAgree', 'Agree and continue')}
        </button>
      </div>
    </div>
  </div>
);

const RegisterForm = ({
  error,
  onSubmit,
  onGoogleRegister,
  onAppleRegister,
  canShowAppleAuth,
  t,
}) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordCheck, setPasswordCheck] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [aiDataProcessingConsent, setAiDataProcessingConsent] = useState(false);
  const [showAiConsentModal, setShowAiConsentModal] = useState(false);

  const canSubmit = agreedToTerms && confirmedAge && aiDataProcessingConsent;

  return (
    <div className="relative z-10 max-w-md w-full space-y-8 bg-slate-900/60 p-10 rounded-[2rem] border border-white/10 shadow-2xl backdrop-blur-2xl mt-4 mb-4">
      <div className="text-center">
        <h1 className="text-4xl font-extrabold tracking-tight text-white mb-3">{t('auth.registerTitle', 'Create your account')}</h1>
        <p className="text-gray-400 font-medium tracking-wide">{t('auth.registerSubtitle', 'Set up your profile and unlock your first plan.')}</p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            username,
            email,
            password,
            passwordCheck,
            agreedToTerms,
            confirmedAge,
            aiDataProcessingConsent,
            aiDataProcessingConsentVersion: AI_CONSENT_VERSION,
            resetForm() {
              setUsername('');
              setEmail('');
              setPassword('');
              setPasswordCheck('');
              setAgreedToTerms(false);
              setConfirmedAge(false);
              setAiDataProcessingConsent(false);
            },
          });
        }}
        className="mt-8 space-y-6"
      >
        <div className="space-y-4">
          <input className={inputClassName} type="text" autoComplete="username" required placeholder={t('auth.username', 'Username')} value={username} onChange={(event) => setUsername(event.target.value)} />
          <input className={inputClassName} type="email" inputMode="email" autoComplete="email" required placeholder={t('auth.email', 'Email address')} value={email} onChange={(event) => setEmail(event.target.value)} />
          <input className={inputClassName} type="password" autoComplete="new-password" required placeholder={t('auth.password', 'Password')} value={password} onChange={(event) => setPassword(event.target.value)} />
          <input className={inputClassName} type="password" autoComplete="new-password" required placeholder={t('auth.confirmPassword', 'Confirm password')} value={passwordCheck} onChange={(event) => setPasswordCheck(event.target.value)} />
        </div>

        <div className="flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(event) => setAgreedToTerms(event.target.checked)}
              className="h-4 w-4 rounded border-gray-600 bg-slate-950/50 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900 transition-colors"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="font-medium text-gray-300">{t('auth.termsAgreement', 'I have read and agree to the Terms of Service and Privacy Policy.')}</label>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                {t('auth.termsOfService', 'Terms of Service')}
              </Link>
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                {t('auth.privacyPolicy', 'Privacy Policy')}
              </Link>
            </div>
          </div>
        </div>

        <div className="flex items-start mt-4">
          <div className="flex h-5 items-center">
            <input
              id="age-confirm"
              name="age-confirm"
              type="checkbox"
              checked={confirmedAge}
              onChange={(event) => setConfirmedAge(event.target.checked)}
              className="h-4 w-4 rounded border-gray-600 bg-slate-950/50 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900 transition-colors"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="age-confirm" className="font-medium text-gray-300">{t('auth.ageConfirm', 'I confirm that I am at least 16 years old.')}</label>
          </div>
        </div>

        <div className="flex items-start mt-4">
          <div className="flex h-5 items-center">
            <input
              id="ai-data-processing-consent"
              name="ai-data-processing-consent"
              type="checkbox"
              checked={aiDataProcessingConsent}
              onChange={() => setShowAiConsentModal(true)}
              className="h-4 w-4 rounded border-gray-600 bg-slate-950/50 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900 transition-colors"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="ai-data-processing-consent" className="font-medium text-gray-300">
              {t('auth.aiConsent', 'I consent to the required AI data processing.')} {' '}
              <button
                type="button"
                onClick={() => setShowAiConsentModal(true)}
                className="text-cyan-400 hover:text-cyan-300"
              >
                {t('auth.viewDetails', 'View details')}
              </button>
            </label>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl bg-red-950/50 p-4 border border-red-900/50 backdrop-blur-sm text-sm font-medium text-red-200">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          className={`group relative flex w-full justify-center rounded-xl px-4 py-4 text-sm font-bold text-white transition-all shadow-lg active:scale-[0.98] ${
            canSubmit
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900'
              : 'bg-slate-800 cursor-not-allowed opacity-50 text-gray-400'
          }`}
        >
          {t('auth.register', 'Create account')}
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-slate-900/60 px-4 text-gray-400 backdrop-blur-sm rounded-full">{t('auth.continueWith', 'Or continue with')}</span>
          </div>
        </div>

        <div>
          {canShowAppleAuth ? (
            <AppleAuthButton
              label={t('auth.signUpWithApple', 'Sign up with Apple')}
              disabled={!canSubmit}
              onClick={() => onAppleRegister({ agreedToTerms, confirmedAge, aiDataProcessingConsent, aiDataProcessingConsentVersion: AI_CONSENT_VERSION })}
              className="mb-3"
            />
          ) : null}

          <button
            type="button"
            onClick={() => onGoogleRegister({ agreedToTerms, confirmedAge, aiDataProcessingConsent, aiDataProcessingConsentVersion: AI_CONSENT_VERSION })}
            className={`flex w-full justify-center items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-sm font-semibold text-white hover:bg-white/10 backdrop-blur-sm active:scale-[0.98] ${!canSubmit ? 'opacity-50' : ''}`}
            disabled={!canSubmit}
          >
            <GoogleLogo />
            {t('auth.signUpWithGoogle', 'Sign up with Google')}
          </button>
        </div>

        <div className="text-center pt-2">
          <Link to="/login" className="text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors">
            {t('auth.alreadyHaveAccount', 'Already have an account? Sign in')}
          </Link>
        </div>
      </form>

      {showAiConsentModal ? (
        <AiConsentModal
          t={t}
          onAgree={() => {
            setAiDataProcessingConsent(true);
            setShowAiConsentModal(false);
          }}
          onDecline={() => {
            setAiDataProcessingConsent(false);
            setShowAiConsentModal(false);
          }}
        />
      ) : null}
    </div>
  );
};

const VerificationSentPanel = ({ email, initiallySent, onReset, t }) => {
  const [secondsRemaining, setSecondsRemaining] = useState(initiallySent ? 60 : 0);
  const [status, setStatus] = useState(initiallySent ? 'sent' : 'failed');
  const [error, setError] = useState('');

  useEffect(() => {
    if (secondsRemaining <= 0) return undefined;
    const timer = window.setInterval(() => {
      setSecondsRemaining((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [secondsRemaining]);

  const handleResend = async () => {
    setStatus('sending');
    setError('');
    try {
      const response = await resendVerificationEmail(email);
      setSecondsRemaining(response.data?.retryAfterSeconds || 60);
      setStatus('sent');
    } catch (requestError) {
      const retryAfter = requestError?.response?.data?.retryAfterSeconds;
      if (retryAfter) setSecondsRemaining(retryAfter);
      setStatus('failed');
      setError(requestError?.response?.data?.msg || t('auth.verificationResendError', 'Unable to send another verification email right now.'));
    }
  };

  return (
    <div className="relative z-10 w-full max-w-md rounded-[2rem] border border-cyan-300/20 bg-slate-900/80 p-8 text-center shadow-2xl backdrop-blur-2xl sm:p-10" role="status">
      <MailCheck className="mx-auto h-12 w-12 text-cyan-300" aria-hidden="true" />
      <h1 className="mt-5 text-3xl font-black text-white">{t('auth.verificationSentTitle', 'Verification email sent')}</h1>
      <p className="mt-4 leading-7 text-gray-300">
        {t('auth.verificationSentBody', 'We sent a verification link to {email}. Open the link to confirm your email and activate your account. Check your inbox and spam or junk folder.', { email })}
      </p>
      {status === 'failed' ? (
        <p className="mt-4 rounded-xl border border-amber-300/20 bg-amber-500/10 p-3 text-sm text-amber-100">
          {error || t('auth.verificationInitialFailure', 'Your account was created, but the verification email could not be sent. Request another email below.')}
        </p>
      ) : null}
      <button
        type="button"
        onClick={handleResend}
        disabled={secondsRemaining > 0 || status === 'sending'}
        className="mt-6 w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {status === 'sending'
          ? t('auth.verificationResending', 'Sending...')
          : t('auth.verificationResend', 'Resend verification email')}
      </button>
      {secondsRemaining > 0 ? (
        <p className="mt-3 text-sm text-gray-400">
          {t('auth.verificationRetryCountdown', 'You can request another email in {seconds} seconds.', { seconds: secondsRemaining })}
        </p>
      ) : null}
      <button type="button" onClick={onReset} className="mt-6 text-sm font-semibold text-cyan-300 hover:text-cyan-200">
        {t('auth.backToSignIn', 'Back to sign in')}
      </button>
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isNativeApp, caps, postToNative } = useNativeCaps();
  const { t } = useLocale();
  const canShowAppleAuth = canUseNativeAppleAuth({ isNativeApp, caps });
  const [error, setError] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const authError = getAuthQueryErrorMessage(queryParams.get('error'), t);
    if (authError) {
      setError(authError);
    }
    if (queryParams.get('google_login_failed') === 'true') {
      setError(t('auth.googleLoginFailed', 'No account was found. Create an account first, then sign in.'));
      navigate('/register', { replace: true });
    }
  }, [location.search, navigate, t]);

  const handleRegister = async ({
    username,
    email,
    password,
    passwordCheck,
    agreedToTerms,
    confirmedAge,
    aiDataProcessingConsent,
    aiDataProcessingConsentVersion,
    resetForm,
  }) => {
    setError('');

    if (!agreedToTerms || !confirmedAge || !aiDataProcessingConsent) {
      setError(t('auth.registerRequirements', 'To create your account, you need to accept the Terms and Privacy Policy, confirm you are at least 16, and consent to the required AI data processing.'));
      return;
    }

    try {
      const response = await register(
        username,
        email,
        password,
        passwordCheck,
        agreedToTerms,
        confirmedAge,
        aiDataProcessingConsent,
        aiDataProcessingConsentVersion
      );
      setVerificationResult({
        email: response.data?.email || email,
        sent: response.data?.verificationEmailSent !== false,
      });
      resetForm();
    } catch (err) {
      const nextError = err?.response?.data?.msg || err?.response?.data?.message || t('auth.unexpectedError', 'Something went wrong. Please try again.');
      setError(nextError);
    }
  };

  const handleGoogleRegister = async ({ agreedToTerms, confirmedAge, aiDataProcessingConsent, aiDataProcessingConsentVersion }) => {
    setError('');

    if (!agreedToTerms || !confirmedAge || !aiDataProcessingConsent) {
      setError(t('auth.registerRequirements', 'To create your account, you need to accept the Terms and Privacy Policy, confirm you are at least 16, and consent to the required AI data processing.'));
      return;
    }

    const query = new URLSearchParams({
      action: 'register',
      agreedToTerms: String(agreedToTerms),
      confirmedAge: String(confirmedAge),
      aiDataProcessingConsent: String(aiDataProcessingConsent),
      aiDataProcessingConsentVersion,
    });

    window.location.href = `${config.api.baseUrl}/users/auth/google?${query.toString()}`;
  };

  const handleAppleRegister = ({ agreedToTerms, confirmedAge, aiDataProcessingConsent, aiDataProcessingConsentVersion }) => {
    setError('');

    if (!agreedToTerms || !confirmedAge || !aiDataProcessingConsent) {
      setError(t('auth.registerRequirements', 'To create your account, you need to accept the Terms and Privacy Policy, confirm you are at least 16, and consent to the required AI data processing.'));
      return;
    }

    const posted = postToNative({
      type: 'FORZA_APPLE_AUTH',
      action: 'register',
      agreedToTerms,
      confirmedAge,
      aiDataProcessingConsent,
      aiDataProcessingConsentVersion,
    });

    if (!posted) {
      setError(t('auth.appleUnavailable', 'Apple Sign In is unavailable in this environment.'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B0F19] via-[#111827] to-[#0A192F] text-white px-4 py-12 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-cyan-600/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />

      {verificationResult ? (
        <VerificationSentPanel
          email={verificationResult.email}
          initiallySent={verificationResult.sent}
          onReset={() => navigate('/login')}
          t={t}
        />
      ) : (
        <RegisterForm
          error={error}
          onSubmit={handleRegister}
          onGoogleRegister={handleGoogleRegister}
          onAppleRegister={handleAppleRegister}
          canShowAppleAuth={canShowAppleAuth}
          t={t}
        />
      )}
    </div>
  );
};

export default RegisterPage;
